import React, { useEffect, useState } from "react";
import { api, CAD, stageClass, STAGES } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, TrendingUp, Award, DollarSign, GraduationCap, Clock, FileCheck, Building2, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/auth";
import NewApplicationDialog from "../components/NewApplicationDialog";
import DeadlineBoxes from "../components/DeadlineBoxes";

const KPI = ({ label, value, icon: Icon, tone = "slate", testid, sub }) => (
  <div data-testid={testid}
       className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
        <div className="heading-font text-2xl font-bold mt-1 text-slate-900 truncate">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${tone}-50 text-${tone}-600 shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState(null);
  const [meta, setMeta] = useState({ partners: [], colleges: [], intakes: [], users: [] });
  const [filters, setFilters] = useState({});
  const [openNew, setOpenNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([_,v]) => v && v !== "all"));
    const [s, a, m] = await Promise.all([
      api.get(`/dashboard/stats?${qs}`),
      api.get(`/applications?${qs}`),
      api.get(`/meta`),
    ]);
    setStats(s.data); setApps(a.data.slice(0, 8)); setMeta(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const canFinance = ["admin","finance","management"].includes(user?.role);
  const bdms = meta.users.filter(u => ["bdm","admin"].includes(u.role));
  const admissionsUsers = meta.users.filter(u => ["admissions_officer","admin"].includes(u.role));

  const FSelect = ({ k, placeholder, items }) => (
    <Select value={filters[k] || "all"} onValueChange={(v)=>setFilters({...filters, [k]: v})}>
      <SelectTrigger data-testid={`filter-${k}`} className="h-9 w-[160px] bg-white">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {placeholder}</SelectItem>
        {items.map(it => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name}. Here's a live look at your pipeline.</p>
        </div>
        <Button data-testid="add-application-btn" onClick={() => setOpenNew(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Application
        </Button>
      </div>

      <DeadlineBoxes />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-medium pl-1 pr-1">Filters</span>
        <input type="date" data-testid="filter-date-from" value={filters.date_from||""}
               onChange={e=>setFilters({...filters, date_from: e.target.value})}
               className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white" />
        <input type="date" data-testid="filter-date-to" value={filters.date_to||""}
               onChange={e=>setFilters({...filters, date_to: e.target.value})}
               className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white" />
        <FSelect k="partner_id" placeholder="Partner" items={meta.partners.map(p=>({value: p.id, label: p.company_name}))} />
        <FSelect k="college" placeholder="College" items={(meta.colleges||[]).map(c=>({value: c.id, label: c.code ? `${c.code} — ${c.name}` : c.name}))} />
        <FSelect k="intake" placeholder="Intake" items={(meta.intakes||[]).map(i=>({value: i.id, label: i.name}))} />
        <FSelect k="bdm_id" placeholder="BDM" items={bdms.map(u=>({value: u.user_id, label: u.name}))} />
        <FSelect k="admissions_officer_id" placeholder="Admissions" items={admissionsUsers.map(u=>({value: u.user_id, label: u.name}))} />
        <FSelect k="stage" placeholder="Stage" items={STAGES.map(s=>({value: s, label: s}))} />
        {Object.keys(filters).length > 0 && (
          <Button variant="ghost" size="sm" onClick={()=>setFilters({})} data-testid="clear-filters-btn">Clear</Button>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {stats === null && <div data-testid="kpi-loading" className="col-span-full text-sm text-slate-500">Loading figures…</div>}
        {stats !== null && <>
        <KPI testid="kpi-total-applications" label="Total Applications" value={stats.total_applications ?? 0} icon={FileText} tone="blue" />
        <KPI testid="kpi-new-applications" label="New Applications" value={stats.new_applications ?? 0} icon={Plus} tone="sky" />
        <KPI testid="kpi-docs-pending" label="Documents Pending" value={stats.documents_pending ?? 0} icon={Clock} tone="amber" />
        <KPI testid="kpi-ready-to-apply" label="Ready to Apply" value={stats.ready_to_apply ?? 0} icon={FileCheck} tone="sky" />
        <KPI testid="kpi-applied" label="Applications Submitted" value={stats.applications_submitted ?? 0} icon={FileText} tone="indigo" />
        <KPI testid="kpi-under-review" label="Under Review" value={stats.under_review ?? 0} icon={Building2} tone="blue" />
        <KPI testid="kpi-offers-received" label="Offers Received" value={stats.offers_received ?? 0} icon={Award} tone="emerald" />
        <KPI testid="kpi-fees-pending" label="Fees Pending" value={stats.fees_pending ?? 0} icon={DollarSign} tone="yellow" />
        <KPI testid="kpi-pal-pending" label="PAL Pending" value={stats.pal_pending ?? 0} icon={Clock} tone="rose" />
        <KPI testid="kpi-admissions-completed" label="Admissions Completed" value={stats.admissions_completed ?? 0} icon={GraduationCap} tone="green" />
        <KPI testid="kpi-sem1-enrolled" label="Semester 1 Enrolled" value={stats.semester1_enrolled ?? 0} icon={Users} tone="teal" />
        <KPI testid="kpi-sem2-enrolled" label="Semester 2 Enrolled" value={stats.semester2_enrolled ?? 0} icon={Users} tone="teal" />
        {canFinance && <>
          <KPI testid="kpi-comm-outstanding" label="Commission Outstanding" value={CAD(stats.commission_outstanding||0)} icon={TrendingUp} tone="orange" />
          <KPI testid="kpi-comm-received" label="Commission Received" value={CAD(stats.commission_received||0)} icon={DollarSign} tone="green" />
          <KPI testid="kpi-partner-payable" label="Partner Payments Due" value={CAD(stats.partner_payments_due||0)} icon={Building2} tone="rose" />
        </>}
        </>}
      </div>

      {/* Recent applications */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 flex items-center justify-between border-b border-slate-200">
          <h2 className="heading-font text-lg font-semibold text-slate-900">Recent Applications</h2>
          <Button variant="ghost" size="sm" onClick={()=>nav("/applications")} data-testid="view-all-apps">View all</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="text-left px-4 py-3">App ID</th><th className="text-left px-4 py-3">Student</th>
                <th className="text-left px-4 py-3">College</th><th className="text-left px-4 py-3">Intake</th>
                <th className="text-left px-4 py-3">Stage</th></tr>
          </thead>
          <tbody>
            {(apps || []).map(a => (
              <tr key={a.id} data-testid={`row-app-${a.application_id}`}
                  onClick={()=>nav(`/applications/${a.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 mono text-xs text-slate-700">{a.application_id}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{a.student_name}</td>
                <td className="px-4 py-3 text-slate-700">{a.college}</td>
                <td className="px-4 py-3 text-slate-700">{a.intake}</td>
                <td className="px-4 py-3"><span className={`stage-badge ${stageClass(a.stage)}`}>{a.stage}</span></td>
              </tr>
            ))}
            {apps === null && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading…</td></tr>}
            {apps !== null && apps.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">No applications yet. Add one to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <NewApplicationDialog open={openNew} onOpenChange={setOpenNew} meta={meta} onCreated={(a)=>{ setOpenNew(false); nav(`/applications/${a.id}`); }} />
    </div>
  );
}
